/**
 * Sales Performance round 2, stage 3: training records. Pure and side-effect free.
 *
 * A training record says that someone was (or is to be) trained on one product category. This file decides
 * what the numbers say about it, and nothing else: it stores nothing and sends nothing.
 *
 * Low sales start a conversation, they do not prove missing knowledge (Yaron's brief, section 7): demand, stock
 * and the customers' suitability matter too. The wording here is careful about that.
 *
 * Dates are YYYY-MM-DD strings compared as text. There is no `Date` and no time zone in here: the months come
 * with the dates of the calendar they belong to (its `monthRanges`), so a 26th-to-25th calendar works as it is.
 */

export type TrainingStatus = 'assigned' | 'completed' | 'cancelled';

export interface TrainingNote {
    /** YYYY-MM-DD */
    at: string;
    byName: string;
    text: string;
}

/** `training/{id}` under the Sales Performance config. */
export interface TrainingRecord {
    id: string;
    rep: string;
    repName: string;
    category: string;
    categoryName: string;
    status: TrainingStatus;
    /** YYYY-MM-DD; may be backdated so a real past session shows real numbers */
    assignedAt: string;
    dueAt?: string;
    completedAt?: string;
    /** the signed-in user's uid and name when it was assigned */
    assignedBy: string;
    assignedByName: string;
    trainer?: string;
    notes?: TrainingNote[];
    /** reserved: a verdict someone chose to freeze; the page computes it live and does not write it */
    outcome?: string;
    /** the record this one follows up */
    followUpOf?: string;
}

/** One complete-or-not month of a salesperson's figures in one category. */
export interface TrainingMonth {
    key: string;
    /** first and last day of the month in its calendar, YYYY-MM-DD */
    start: string;
    end: string;
    /** share of the salesperson's active customers who bought the category, 0..1 */
    reach: number;
    sales: number;
    gp: number;
}

export interface TrainingWindow {
    /** how many complete months the averages cover */
    months: number;
    reach: number;
    sales: number;
    gp: number;
}

export type TrainingVerdict = 'improved' | 'no-change' | 'declined' | 'too-early' | 'pending' | 'no-baseline';

export const TRAINING_VERDICT_LABELS: Record<TrainingVerdict, string> = {
    improved: 'Improved',
    'no-change': 'No change',
    declined: 'Declined',
    'too-early': 'Too early to say',
    pending: 'Not completed yet',
    'no-baseline': 'No earlier figures',
};

export interface TrainingResult {
    before: TrainingWindow | null;
    after: TrainingWindow | null;
    verdict: TrainingVerdict;
    label: string;
}

/** Complete months averaged before the assignment. */
export const TRAINING_BEFORE_MONTHS = 3;
/** Reach must move by at least this many points ... */
export const TRAINING_MIN_POINTS = 0.02;
/** ... and by at least this share of where it was. */
export const TRAINING_MIN_RELATIVE = 0.1;

const EPS = 1e-9;

function average(months: TrainingMonth[]): TrainingWindow | null {
    if (!months.length) return null;
    const mean = (f: (m: TrainingMonth) => number) => months.reduce((n, m) => n + f(m), 0) / months.length;
    return { months: months.length, reach: mean((m) => m.reach), sales: mean((m) => m.sales), gp: mean((m) => m.gp) };
}

/**
 * What the numbers say about one training.
 *
 * Before = the up to 3 complete months that END before it was assigned. After = every complete month that STARTS
 * after it was completed. A month is complete when it ends on or before `lastLine`, the last day with sales
 * loaded; a month in progress never counts. Fewer than one complete month after: "Too early to say".
 *
 * Verdict: Improved when reach is up by at least 2 points and by at least 10% of where it was; Declined when down
 * by the same; otherwise No change. From a before of zero any rise of 2 points counts.
 */
export function trainingResult(
    record: Pick<TrainingRecord, 'status' | 'assignedAt' | 'completedAt'>,
    months: TrainingMonth[],
    lastLine: string | null | undefined
): TrainingResult {
    const done = (months || [])
        .filter((m) => !lastLine || !m.end || m.end <= lastLine.slice(0, 10))
        .sort((a, b) => a.key.localeCompare(b.key));
    const before = average(done.filter((m) => m.end < record.assignedAt).slice(-TRAINING_BEFORE_MONTHS));
    const finish = (verdict: TrainingVerdict, after: TrainingWindow | null): TrainingResult => ({
        before,
        after,
        verdict,
        label: TRAINING_VERDICT_LABELS[verdict],
    });
    if (record.status !== 'completed' || !record.completedAt) return finish('pending', null);
    const completedAt = record.completedAt;
    const after = average(done.filter((m) => m.start > completedAt));
    if (!after) return finish('too-early', null);
    if (!before) return finish('no-baseline', after);
    const change = after.reach - before.reach;
    const enough = (c: number) =>
        c >= TRAINING_MIN_POINTS - EPS && (before.reach <= 0 || c / before.reach >= TRAINING_MIN_RELATIVE - EPS);
    if (enough(change)) return finish('improved', after);
    if (enough(-change)) return finish('declined', after);
    return finish('no-change', after);
}

export interface TrainingStats {
    suggested: number;
    /** open: assigned and not completed or cancelled */
    assigned: number;
    overdue: number;
    completed: number;
    improved: number;
}

const isOverdue = (t: TrainingRecord, today: string) => t.status === 'assigned' && !!t.dueAt && t.dueAt < today;

/** The five figures at the top of the Training tab. `verdicts` maps a record id to its trainingResult verdict. */
export function trainingStats(
    records: TrainingRecord[],
    verdicts: Record<string, TrainingVerdict>,
    suggested: number,
    today: string
): TrainingStats {
    return {
        suggested,
        assigned: records.filter((t) => t.status === 'assigned').length,
        overdue: records.filter((t) => isOverdue(t, today)).length,
        completed: records.filter((t) => t.status === 'completed').length,
        improved: records.filter((t) => t.status === 'completed' && verdicts[t.id] === 'improved').length,
    };
}

export const trainingKey = (rep: string, category: string) => `${rep}|${category}`;

/** Salesperson-category pairs with training assigned and not yet completed or cancelled. */
export function openTrainingKeys(records: TrainingRecord[]): Set<string> {
    return new Set(records.filter((t) => t.status === 'assigned').map((t) => trainingKey(t.rep, t.category)));
}

/** The shape of a coaching row this needs (the page's own CoachingRow fits). */
export interface SuggestionSource {
    rep: string;
    name: string;
    gaps: { key: string; name: string; reach: number; companyReach: number; missed: number }[];
}

export interface TrainingSuggestion {
    rep: string;
    repName: string;
    category: string;
    categoryName: string;
    reach: number;
    companyReach: number;
    missed: number;
}

/** One suggestion per coaching gap, leaving out any salesperson and category that already has open training. */
export function trainingSuggestions(rows: SuggestionSource[], records: TrainingRecord[]): TrainingSuggestion[] {
    const open = openTrainingKeys(records);
    return rows.flatMap((r) =>
        r.gaps
            .filter((g) => !open.has(trainingKey(r.rep, g.key)))
            .map((g) => ({
                rep: r.rep,
                repName: r.name,
                category: g.key,
                categoryName: g.name,
                reach: g.reach,
                companyReach: g.companyReach,
                missed: g.missed,
            }))
    );
}

const realDate = (s: string | undefined): s is string => {
    if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
    const d = new Date(`${s}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
};

/** Everything wrong with a training form, in plain words. Empty means it can be saved. */
export function validateTraining(f: {
    rep: string;
    category: string;
    assignedAt: string;
    dueAt?: string;
    completedAt?: string;
}): string[] {
    const out: string[] = [];
    if (!f.rep) out.push('Choose a salesperson.');
    if (!f.category) out.push('Choose a category.');
    const assigned = realDate(f.assignedAt);
    if (!assigned) out.push('Enter the date it was assigned.');
    if (f.dueAt) {
        if (!realDate(f.dueAt)) out.push('Enter the due date as a real date, or leave it empty.');
        else if (assigned && f.dueAt < f.assignedAt) out.push('The due date cannot be before the assigned date.');
    }
    if (f.completedAt) {
        if (!realDate(f.completedAt)) out.push('Enter the completed date as a real date.');
        else if (assigned && f.completedAt < f.assignedAt)
            out.push('The completed date cannot be before the assigned date.');
    }
    return out;
}
